import type {
  InventoryResource,
  Snapshot,
  FsxOntapVolume,
  FsxVolumeBackupInfo,
  SourceAccount,
} from '@repo/api-gw-sdk';
import { AccountState, Provider } from '@repo/api-gw-sdk';

import { Loader } from '@/components/layout/loading';
import { useDAL } from '@/contexts/useDAL';
import { useSourceAccounts } from '@/contexts/useSourceAccounts';

import { FSxConfiguration } from './steps/fsxConfiguration';

import type { Encryption } from '../encryption/encryptionComponent';
import type { Step } from '../wizard/Step';
import { Wizard } from '../wizard/Wizard';

export type FsxFileSystemType = 'WINDOWS' | 'LUSTRE' | 'ONTAP' | 'OPENZFS';

/** Deployment type from AWS (e.g. SINGLE_AZ_1, MULTI_AZ_1). Multi-AZ requires standby subnet for restore. */
export type FsxDeploymentType = string;

export interface RestoreFSxState {
  resourceId: string;
  snapshot: Snapshot;
  fileSystemName?: string;
  fileSystemType?: FsxFileSystemType;
  /** Deployment type (e.g. SINGLE_AZ_1, MULTI_AZ_1). Used to determine if standby subnet is required. */
  deploymentType?: FsxDeploymentType;
  supportedCloudProviders: Provider[];
  restoreAccount?: SourceAccount;
  accounts?: SourceAccount[];
  emptyMessage?: string;
  regionName?: string;
  subnetIds: string[];
  securityGroupIds: string[];
  vpc?: string;
  initialTags: Record<string, string>;
  tags: Record<string, string>;
  keepOriginalTags: boolean;
  encryption?: Encryption;
  initialRegionName: string;
  storageCapacity?: number;
  throughputCapacityMBps?: number;
  // ONTAP-specific restore options
  junctionPath?: string;
  storageVirtualMachineId?: string;
  volumeName?: string;
  volumeSize?: number; // Size in bytes
  storageEfficiencyEnabled?: boolean;
  // Original SVM ID from the backed-up volume (for default selection)
  originalSvmId?: string;
  // Original file system ID (for fetching SVMs)
  originalFileSystemId?: string;
  // Selected volume index to restore (for ONTAP multi-volume snapshots)
  selectedVolumeIndex?: number;
  // Existing volumes on the target file system (for conflict detection)
  existingOntapVolumes?: FsxOntapVolume[];
  // Volumes being restored from the snapshot
  backedUpVolumes?: FsxVolumeBackupInfo[];
}

const restoreFSxFlow: Step<RestoreFSxState> = {
  id: 'fsx_configuration',
  Component: FSxConfiguration,
};

export const RestoreFSxWizard = ({
  resource,
  snapshot,
  onClose,
  onBack,
  selectedVolumeIndex,
}: {
  resource: InventoryResource;
  snapshot: Snapshot;
  onClose: () => void;
  onBack: () => void;
  /** Pre-selected volume index for ONTAP (selected from snapshot list) */
  selectedVolumeIndex?: number;
}) => {
  const { sourceAccounts } = useSourceAccounts();
  const dal = useDAL();
  const { data: restoreAccountsResponse } = dal.cloudAccounts.restore.useList();

  // FSx backups are stored in AWS Backup Vault in the source account
  // Only show the source account (no cross-account restore support)
  const sourceAccount = sourceAccounts?.find(
    (account) =>
      account.providerAccountId === resource.providerAccountId &&
      account.sourceAccountAttributes.cloudProvider === Provider.Aws &&
      account.status === AccountState.ACCOUNT_STATE_CONNECTED
  );

  // FSx restore requires a restore account onboarded for the same AWS account
  const hasRestoreAccount = restoreAccountsResponse?.accounts?.some(
    (account) =>
      account.providerAccountId === resource.providerAccountId &&
      account.state === AccountState.ACCOUNT_STATE_CONNECTED
  );

  // Filter accounts to only show the source account
  const allowedAccounts =
    sourceAccount && hasRestoreAccount ? [sourceAccount] : [];

  const fileSystemType = resource.resourceProperties?.awsFsx
    ?.fileSystemType as FsxFileSystemType;
  const deploymentType = resource.resourceProperties?.awsFsx?.deploymentType;
  const isOntap = fileSystemType === 'ONTAP';

  // Wait for accounts data to load before rendering the wizard
  if (!sourceAccounts || !restoreAccountsResponse) {
    return <Loader />;
  }

  return (
    <Wizard<RestoreFSxState>
      onAbort={(origin) => (origin === 'back' ? onBack() : onClose())}
      onFinish={async () => true}
      flow={[restoreFSxFlow]}
      initialState={{
        initialRegionName: resource.region || 'us-east-1',
        snapshot,
        resourceId: snapshot.resourceId,
        fileSystemType,
        deploymentType,
        supportedCloudProviders: [Provider.Aws],
        restoreAccount: sourceAccount,
        accounts: allowedAccounts,
        keepOriginalTags: true,
        initialTags: snapshot.resource?.tags || {},
        tags: snapshot.resource?.tags || {},
        subnetIds: [],
        securityGroupIds: [],
        emptyMessage: !sourceAccount
          ? 'The source account for this FSx file system is not connected. FSx backups are stored in AWS Backup Vault in the source account and can only be restored to the same account.'
          : !hasRestoreAccount
            ? `A restore account is required for the same AWS account (${resource.providerAccountId}). Please onboard a restore account for this AWS account.`
            : undefined,
        // ONTAP-specific: volumes from backup and existing volumes for conflict detection
        backedUpVolumes: snapshot.fsxSnapshotProperties?.backedUpVolumes,
        existingOntapVolumes: resource.resourceProperties?.awsFsx?.ontapVolumes,
        // Pre-selected volume index from snapshot list (ONTAP only)
        selectedVolumeIndex,
      }}
      title={isOntap ? 'Restore Volume' : 'Restore FSx'}
      stepperLabels={['Configuration']}
    />
  );
};
